import { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import '../../assets/css/case-property.css';
// @ts-ignore
import { useProperty, usePage, useVibesites } from '@vibesites/runtime/react';
import { resolveScopedOverride, resolveSlotGroupFields } from '@vibesites/runtime';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FURNISHED_CTA_DEF } from './PropertyHero';
import { getPropertyBasePath, formatPhone, phoneDigits, cleanLinkUrl } from './propertyUtils';
import { RangeSlider } from './RangeSlider';
import loginIcon from '../../assets/images/login.svg';
import { openTourScheduler } from './TourSchedulerModal';

export function PropertyPageHeader() {
  const property = useProperty();
  const page = usePage();
  const { slotGroupOverrides } = useVibesites();
  const [navOpen, setNavOpen] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 3000]);
  const [selectedBeds, setSelectedBeds] = useState('');
  const [selectedBaths, setSelectedBaths] = useState('');

  const alias = page?.alias || '';
  const isFloorPlansPage = alias.startsWith('property-floor');

  // Derive price bounds from floorplan data
  const { priceMin, priceMax } = useMemo(() => {
    const floorplans = property.floorplans || [];
    const rents: number[] = [];
    floorplans.forEach((fp: any) => {
      // Check both price_min and price_max to get the full range
      for (const key of ['floorplan_price_min', 'floorplan_price_max']) {
        const raw = fp[key];
        if (raw == null || raw === '') continue;
        const cleaned = String(raw).replace(/[$,]/g, '');
        const r = parseFloat(cleaned);
        if (isFinite(r) && r > 0) rents.push(r);
      }
    });
    if (rents.length === 0) return { priceMin: 0, priceMax: 3000 };
    const min = Math.floor(Math.min(...rents) / 50) * 50;
    const max = Math.ceil(Math.max(...rents) / 50) * 50;
    return { priceMin: min, priceMax: max === min ? max + 50 : max };
  }, [property.floorplans]);

  // Derive unique bedroom options from floorplan data
  const bedOptions = useMemo(() => {
    const floorplans = property.floorplans || [];
    const beds = new Set<number>();
    floorplans.forEach((fp: any) => {
      if (fp.floorplan_beds_min == null || fp.floorplan_beds_min === '') return;
      const b = Number(fp.floorplan_beds_min);
      if (!isFinite(b) || b < 0) return;
      beds.add(Math.round(b));
    });
    return Array.from(beds).sort((a, b) => a - b);
  }, [property.floorplans]);

  // Derive unique bathroom options from floorplan data
  const bathOptions = useMemo(() => {
    const floorplans = property.floorplans || [];
    const baths = new Set<number>();
    floorplans.forEach((fp: any) => {
      if (fp.floorplan_baths_min == null || fp.floorplan_baths_min === '') return;
      const b = Number(fp.floorplan_baths_min);
      if (!isFinite(b) || b < 0) return;
      baths.add(Math.round(b));
    });
    return Array.from(baths).sort((a, b) => a - b);
  }, [property.floorplans]);

  // Re-sync slider when floorplan data loads async
  useEffect(() => {
    setPriceRange([priceMin, priceMax]);
  }, [priceMin, priceMax]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setShowHeader(false);
        setOpenDropdown(null);
      } else if (currentScrollY < lastScrollY) {
        setShowHeader(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!(e.target as Element).closest('.prop-search-row')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Lock body scroll when nav is open
  useEffect(() => {
    if (navOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [navOpen]);

  // Close nav on escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  const toggleDropdown = useCallback((name: string) => {
    setOpenDropdown(prev => prev === name ? null : name);
  }, []);

  const basePath = getPropertyBasePath(property);
  const phone = property.property_call_tracking_phone || property.property_phone || '';
  const applyUrl = cleanLinkUrl(property.property_apply_now_url);
  const residentUrl = cleanLinkUrl(property.property_resident_portal_url);
  const logo = property.property_primary_logo || property.property_mono_white_logo || property.property_secondary_logo || '';
  const name = property.property_name || '';

  // The Furnished Homes CTA is edited on the property home page hero, so resolve
  // it against the home page scope rather than the current page.
  const sepIdx = alias.indexOf('--');
  const furnishedCta = resolveSlotGroupFields(
    property,
    resolveScopedOverride(slotGroupOverrides, 'pagehero.furnished_cta', {
      alias: sepIdx >= 0 ? `property-home${alias.slice(sepIdx)}` : alias,
      path: basePath,
      template: page?.template,
      baseAlias: 'property-home',
    }) ?? null,
    FURNISHED_CTA_DEF,
  );
  const furnishedUrl = furnishedCta.cta_url || '';
  const furnishedTarget = furnishedCta._target || '_blank';
  const showFurnishedLink = !!furnishedUrl;

  const NAV_LINKS: Array<{ href: string; label: string; openModal?: boolean; event?: string }> = [
    { href: `${basePath}/`, label: 'Home' },
    { href: `${basePath}/floor-plans`, label: 'Floor Plans' },
    { href: `${basePath}/amenities`, label: 'Amenities' },
    { href: `${basePath}/neighborhood`, label: 'Neighborhood' },
    { href: `${basePath}/photos`, label: 'Gallery' },
    { href: '#schedule-tour', label: 'Schedule a Tour', openModal: true, event: 'schedule_tour_clicked' },
    ...(applyUrl ? [{ href: applyUrl, label: 'Apply Online', event: 'apply_now_clicked' }] : []),
    { href: `${basePath}/pet-policy`, label: 'Pet Policy' },
    { href: `${basePath}/contact-us`, label: 'Contact Us' },
    ...(residentUrl ? [{ href: residentUrl, label: 'Resident Login', event: 'resident_portal_clicked' }] : []),
  ];

  const primaryColor = property.property_primary_color || '#b47f00';
  const btnRadius = '5.5rem';

  const isFullRange = priceRange[0] === priceMin && priceRange[1] === priceMax;
  const priceButtonText = isFullRange
    ? 'Price'
    : `$${priceRange[0].toLocaleString()} – $${priceRange[1].toLocaleString()}`;

  return (
    <>
      <style>{`:root { --prop-primary: ${primaryColor}; --prop-btn-radius: ${btnRadius}; }`}</style>

      {/* ── Header Bar ──────────────────────────────────────────── */}
      <header className={`prop-header${showHeader ? '' : ' header-hidden'}`}>
        <div className="prop-header-holder">
          <div className="prop-header-inner">

            {/* Nav area: desktop links + hamburger (order 0 mobile, order 2 desktop) */}
            <div className="prop-header-nav-area">
              {/* Desktop-only links: apply, phone, resident login */}
              <div className="prop-header-links">
                {applyUrl && (
                  <a href={applyUrl} data-vibesite-event="apply_now_clicked">Apply Online</a>
                )}
                {phone && (
                  <a href={`tel:${phoneDigits(phone)}`} className="prop-header-phone-link">
                    <svg className="prop-header-phone-icon" viewBox="0 0 512 512" width="16" height="16" fill="currentColor"><path d="M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64c0 247.4 200.6 448 448 448 18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3l59.9-40.4c13.7-11.2 18.4-30 11.6-46.3l-40-96z"/></svg>
                    <span className="prop-header-phone-text">{formatPhone(phone)}</span>
                  </a>
                )}
                {residentUrl && (
                  <a href={residentUrl} className="prop-header-resident-link" data-vibesite-event="resident_portal_clicked">
                    <img src={loginIcon} alt="" className="prop-header-login-icon" width="16" height="18" />
                    <span className="prop-header-login-label-full">Resident Login</span>
                    <span className="prop-header-login-label-short">Login</span>
                  </a>
                )}
              </div>

              {/* Hamburger toggler */}
              <button
                className="prop-navbar-toggler"
                type="button"
                onClick={() => setNavOpen(prev => !prev)}
                aria-controls="prop-nav-collapse"
                aria-expanded={navOpen}
                aria-label="Toggle navigation"
              >
                <span className="prop-toggler-icon" />
              </button>
            </div>

            {/* Logo (order 1 mobile / order 0 desktop) */}
            <div className="prop-header-logo-wrap">
              {logo && (
                <a className="prop-header-logo" href={`${basePath}/`}>
                  <VibesitesImage url={logo} alt={`${name} logo`} width={180} height={96} />
                </a>
              )}
            </div>

            {/* Mobile-only login link (order 2 on mobile, hidden on desktop) */}
            {residentUrl && (
              <a href={residentUrl} className="prop-header-mobile-login" data-vibesite-event="resident_portal_clicked">
                <img src={loginIcon} alt="" className="prop-header-login-icon" width="14" height="16" />
                <span>Login</span>
              </a>
            )}

            {/* Search bar - all property pages; visually hidden on floor plans to preserve layout */}
              <div className={`prop-header-search${isFloorPlansPage ? ' prop-header-search-hidden' : ''}`}>
                <form className="prop-search-form" action={`${basePath}/floor-plans/`} method="get">
                  <input type="hidden" name="minPrice" value={isFullRange ? '' : String(priceRange[0])} />
                  <input type="hidden" name="maxPrice" value={isFullRange ? '' : String(priceRange[1])} />
                  <input type="hidden" name="beds" value={selectedBeds} />
                  <input type="hidden" name="baths" value={selectedBaths} />
                  <div className="prop-search-row">
                    <div style={{ position: 'relative' }}>
                      <button className={`prop-search-dropdown${openDropdown === 'price' ? ' open' : ''}`} type="button" onClick={() => toggleDropdown('price')}>
                        <span className="prop-search-dropdown-label">{priceButtonText}</span>
                        <svg className="prop-search-dropdown-chevron" viewBox="0 0 12 8" width="10" height="8" fill="currentColor" aria-hidden="true"><path d="M1.41.59 6 5.17 10.59.59 12 2 6 8 0 2z"/></svg>
                      </button>
                      {openDropdown === 'price' && (
                        <div className="prop-dropdown-menu prop-dropdown-price">
                          <div className="prop-price-labels">
                            <span>${priceRange[0].toLocaleString()}</span>
                            <span>${priceRange[1].toLocaleString()}</span>
                          </div>
                          {priceMin < priceMax ? (
                            <RangeSlider
                              min={priceMin}
                              max={priceMax}
                              step={50}
                              value={priceRange}
                              onChange={(v) => setPriceRange(v)}
                            />
                          ) : (
                            <div style={{ fontSize: 13, color: '#666', textAlign: 'center', padding: '8px 0' }}>
                              ${priceMin.toLocaleString()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div style={{ position: 'relative' }}>
                      <button className={`prop-search-dropdown${openDropdown === 'beds' ? ' open' : ''}`} type="button" onClick={() => toggleDropdown('beds')}>
                        <span className="prop-search-dropdown-label">{selectedBeds === '' ? 'Bedrooms' : selectedBeds === '0' ? 'Studio' : `${selectedBeds} Bed`}</span>
                        <svg className="prop-search-dropdown-chevron" viewBox="0 0 12 8" width="10" height="8" fill="currentColor" aria-hidden="true"><path d="M1.41.59 6 5.17 10.59.59 12 2 6 8 0 2z"/></svg>
                      </button>
                      {openDropdown === 'beds' && (
                        <div className="prop-dropdown-menu">
                          <label><input type="radio" checked={selectedBeds === ''} onChange={() => setSelectedBeds('')} /> Any</label>
                          {bedOptions.map(b => (
                            <label key={b}>
                              <input type="radio" checked={selectedBeds === String(b)} onChange={() => setSelectedBeds(String(b))} />
                              {b === 0 ? ' Studio' : ` ${b} Bed`}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    <div style={{ position: 'relative' }}>
                      <button className={`prop-search-dropdown${openDropdown === 'baths' ? ' open' : ''}`} style={{ borderRight: 'none' }} type="button" onClick={() => toggleDropdown('baths')}>
                        <span className="prop-search-dropdown-label">{selectedBaths === '' ? 'Bathrooms' : `${selectedBaths} Bath`}</span>
                        <svg className="prop-search-dropdown-chevron" viewBox="0 0 12 8" width="10" height="8" fill="currentColor" aria-hidden="true"><path d="M1.41.59 6 5.17 10.59.59 12 2 6 8 0 2z"/></svg>
                      </button>
                      {openDropdown === 'baths' && (
                        <div className="prop-dropdown-menu">
                          <label><input type="radio" checked={selectedBaths === ''} onChange={() => setSelectedBaths('')} /> Any</label>
                          {bathOptions.map(b => (
                            <label key={b}>
                              <input type="radio" checked={selectedBaths === String(b)} onChange={() => setSelectedBaths(String(b))} />
                              {` ${b} Bath`}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    <button className="prop-search-submit" type="submit">Find A Home</button>
                  </div>
                </form>
              </div>

          </div>
        </div>
      </header>

      {/* Overlay backdrop when nav is open (outside header to avoid stacking context) */}
      <div
        className={`prop-header-overlay${navOpen ? ' open' : ''}`}
        onClick={() => setNavOpen(false)}
        aria-hidden="true"
      />

      {/* ── Collapsible Nav Panel (outside header for correct z-index) ── */}
      <div
        className={`prop-nav-collapse${navOpen ? ' open' : ''}`}
        id="prop-nav-collapse"
      >
        {/* Mobile-only top section: phone CTA + close button, then apply */}
        <div className="prop-nav-mobile-top">
          <div className="prop-nav-mobile-top-row">
            {phone && (
              <a href={`tel:${phoneDigits(phone)}`} className="prop-btn prop-nav-phone-btn">
                {formatPhone(phone)}
              </a>
            )}
            <button
              className="prop-nav-close"
              type="button"
              onClick={() => setNavOpen(false)}
              aria-label="Close navigation"
            >
              <svg viewBox="0 0 352 512" width="18" height="18" fill="currentColor"><path d="M242.7 256l99.3-99.3c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L197.4 210.7 98.1 111.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L152 256 52.7 355.3c-12.5 12.5-12.5 32.8 0 45.3 6.2 6.2 14.4 9.4 22.6 9.4s16.4-3.1 22.6-9.4L197.4 301.3l99.3 99.3c6.2 6.2 14.4 9.4 22.6 9.4s16.4-3.1 22.6-9.4c12.5-12.5 12.5-32.8 0-45.3L242.7 256z"/></svg>
            </button>
          </div>
          {applyUrl && (
            <a href={applyUrl} className="prop-btn prop-btn-outline prop-nav-apply-btn" data-vibesite-event="apply_now_clicked">
              Apply Online
            </a>
          )}
        </div>

        {/* Desktop-only close button (top-right) */}
        <button
          className="prop-nav-close-desktop"
          type="button"
          onClick={() => setNavOpen(false)}
          aria-label="Close navigation"
        >
          <svg viewBox="0 0 352 512" width="18" height="18" fill="currentColor"><path d="M242.7 256l99.3-99.3c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L197.4 210.7 98.1 111.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L152 256 52.7 355.3c-12.5 12.5-12.5 32.8 0 45.3 6.2 6.2 14.4 9.4 22.6 9.4s16.4-3.1 22.6-9.4L197.4 301.3l99.3 99.3c6.2 6.2 14.4 9.4 22.6 9.4s16.4-3.1 22.6-9.4c12.5-12.5 12.5-32.8 0-45.3L242.7 256z"/></svg>
        </button>

        {/* Nav links */}
        <ul className="prop-nav-list" style={showFurnishedLink ? { gridTemplateRows: 'repeat(7, auto)' } : undefined}>
          {NAV_LINKS.map(l => (
            <Fragment key={l.href}>
            <li className="prop-nav-item">
              <a
                className="prop-nav-link"
                href={l.openModal ? undefined : l.href}
                data-vibesite-event={l.event}
                onClick={(e) => { if (l.openModal) { e.preventDefault(); openTourScheduler(); } setNavOpen(false); }}
              >
                {l.label}
                {l.label === 'Schedule a Tour' && (
                  <span className="prop-nav-tour-icon">
                    <svg viewBox="0 0 512 512" width="12" height="12" fill="var(--prop-primary)"><path d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160zm352-160l-160 160c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L301.3 256 438.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0z"/></svg>
                  </span>
                )}
              </a>
            </li>
            {l.label === 'Floor Plans' && showFurnishedLink && (
              <li className="prop-nav-item">
                <a
                  className="prop-nav-link"
                  href={furnishedUrl}
                  target={furnishedTarget}
                  rel={furnishedTarget === '_blank' ? 'noopener noreferrer' : undefined}
                  onClick={() => setNavOpen(false)}
                >
                  {furnishedCta.cta_text || 'Furnished Homes'}
                </a>
              </li>
            )}
            </Fragment>
          ))}
        </ul>
      </div>
    </>
  );
}

export default PropertyPageHeader;
